import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CustomerService, CustomerDashboard, FeedbackRequest } from '../../api/services/customer.service';
import { RatingService } from '../../api/services/rating.service';
import { HeaderNavComponent } from 'src/app/ui/header-nav/header-nav.component';
import { FooterComponent } from 'src/app/ui/footer/footer.component';

@Component({
  selector: 'app-customer-dashboard',
  standalone: true,
  imports: [
    CommonModule, MatCardModule, MatButtonModule, MatIconModule, MatTabsModule,
    MatSelectModule, MatFormFieldModule, MatInputModule, MatSnackBarModule,
    MatProgressSpinnerModule, FormsModule,
    HeaderNavComponent, FooterComponent
  ],
  templateUrl: './customer-dashboard.component.html'
})
export class CustomerDashboardComponent implements OnInit {
  isLoading = false;
  dashboard: CustomerDashboard | null = null;
  visitHistory: any[] = [];
  expenditureHistory: any[] = [];
  selectedPeriod: 'WEEK' | 'MONTH' | 'YEAR' = 'MONTH';

  showFeedbackDialog = false;
  feedbackSessionId = '';
  feedback: FeedbackRequest = { overallRating: 5, comments: '', customerName: '' };

  ratingStars = [1, 2, 3, 4, 5];

  constructor(
    private customerService: CustomerService,
    private ratingService: RatingService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadDashboard();
    this.loadHistory();
  }

  loadDashboard(): void {
    this.isLoading = true;
    this.customerService.getMyDashboard().subscribe({
      next: (data) => {
        this.dashboard = data;
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  loadHistory(): void {
    this.customerService.getVisitHistory(this.selectedPeriod).subscribe({
      next: (data) => this.visitHistory = data,
      error: () => {}
    });
    this.customerService.getExpenditureHistory(this.selectedPeriod).subscribe({
      next: (data) => this.expenditureHistory = data,
      error: () => {}
    });
  }

  onPeriodChange(): void {
    this.loadHistory();
  }

  openFeedbackDialog(sessionId: string): void {
    this.feedbackSessionId = sessionId;
    this.feedback = { overallRating: 5, comments: '', customerName: '' };
    this.showFeedbackDialog = true;
  }

  closeFeedbackDialog(): void {
    this.showFeedbackDialog = false;
  }

  submitFeedback(): void {
    if (!this.feedbackSessionId || !this.feedback.comments.trim()) {
      this.snackBar.open('Please provide a comment', 'Close', { duration: 3000 });
      return;
    }
    this.customerService.submitFeedback(this.feedbackSessionId,
      { ...this.feedback, type: 'GENERAL' }).subscribe({
      next: () => {
        this.snackBar.open('Feedback submitted. Thank you!', 'Close', { duration: 3000 });
        this.closeFeedbackDialog();
      },
      error: () => this.snackBar.open('Failed to submit feedback', 'Close', { duration: 3000 })
    });
  }

  /**
   * Superseded by the feedback form on this screen.
   *
   * The old implementation posted to /ratings/client/rate, a route from the
   * deleted meethousepros service, so it always failed. A customer's rating of
   * a bar is carried by the feedback they leave for a visit, which is what
   * submitFeedback() above sends.
   */
  submitRating(rating: number, comment: string): void {
    this.snackBar.open('Use the feedback form to rate your visit.', 'Close', { duration: 4000 });
  }

  onBack(): void {
    this.router.navigate(['/customer']);
  }
}
